import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  afterNextRender,
  inject
} from '@angular/core';
import { LucideChevronDown, LucideChevronUp } from '@lucide/angular';
import { ScheduleType } from '../activities/activity-type.model';
import { DayStatusCycleKind, LastCompletedCycleSummary } from '../settings/me.model';
import { DayStatus } from './day-status.util';
import { DayStatusCycleGroup, DayStatusCycleSection, DayStatusCycles } from './day-status-cycles.util';
import { rebuildOrderedIdsForSubset } from './order.util';

export const DAY_STATUS_EXPANDED_STORAGE_KEY = 'lumen.dayStatus.expanded';

@Component({
  selector: 'app-day-status-card',
  standalone: true,
  imports: [CommonModule, CdkDropList, CdkDrag, LucideChevronDown, LucideChevronUp],
  templateUrl: './day-status-card.component.html',
  styleUrls: ['./day-status-card.component.scss']
})
export class DayStatusCardComponent implements OnInit, OnChanges {
  @Input({ required: true }) status!: DayStatus;
  @Input({ required: true }) cycles!: DayStatusCycles;
  @Input() dayLabel = '';
  @Input() reorderDisabled = false;
  @Input() celebrationCycle: DayStatusCycleKind | null = null;
  @Input() celebrationSummary: LastCompletedCycleSummary | null = null;
  @Input() lastCompletedWorkCycle: LastCompletedCycleSummary | null = null;
  @Input() lastCompletedLifeCycle: LastCompletedCycleSummary | null = null;
  @Output() scheduleTypeSelect = new EventEmitter<ScheduleType>();
  @Output() reorderGrouping = new EventEmitter<ScheduleType[]>();

  readonly groupsId = 'day-status-groups';
  readonly listDragStartDelay = { touch: 160, mouse: 0 };
  expanded = false;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  /** Group moved from the keyboard; focus returns to its row once the new order arrives. */
  private pendingFocusOrdinal: ScheduleType | null = null;

  ngOnInit(): void {
    this.expanded = this.loadExpandedPreference();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['cycles'] || !this.pendingFocusOrdinal) {
      return;
    }
    const ordinal = this.pendingFocusOrdinal;
    this.pendingFocusOrdinal = null;
    afterNextRender(() => {
      this.host.nativeElement
        .querySelector<HTMLElement>(`.lm-group-row[data-group-ordinal="${ordinal}"]`)
        ?.focus();
    }, { injector: this.injector });
  }

  trackSection(_index: number, section: DayStatusCycleSection): DayStatusCycleKind {
    return section.cycle;
  }

  trackGroup(_index: number, group: DayStatusCycleGroup): ScheduleType {
    return group.ordinal;
  }

  get cycleSections(): DayStatusCycleSection[] {
    return [this.cycles.work, this.cycles.life];
  }

  getGlanceLine(): string {
    const cycleBits = this.cycleSections
      .filter((section) => section.hasActivities)
      .map((section) => `${section.label} ${section.doneCount}/${section.totalCount}`);
    const pacing = `finish ${this.status.projectedFinishLabel}`;
    return cycleBits.length > 0
      ? `${cycleBits.join(' · ')} · ${pacing}`
      : `${this.getStatusTitle()} · ${pacing} · ${this.getWindowSlackLabel()}`;
  }

  getToggleOverline(): string {
    const status = this.getStatusTitle();
    return this.dayLabel ? `${this.dayLabel} · ${status}` : status;
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
    this.saveExpandedPreference(this.expanded);
  }

  getStatusTitle(): string {
    switch (this.status.overallState) {
      case 'ALL_COMPLETE':
        return 'All groups complete';
      case 'ON_TRACK':
        return 'On track';
      case 'TIGHT':
        return 'Tight but on track';
      case 'OVERRUN':
        return 'Beyond main day window';
      default:
        return 'Day status';
    }
  }

  getWindowSlackLabel(): string {
    if (this.status.windowSlackMinutes >= 0) {
      return `${this.formatMinutes(this.status.windowSlackMinutes)} spare`;
    }
    return `${this.formatMinutes(Math.abs(this.status.windowSlackMinutes))} over`;
  }

  getWindowSlackValue(): string {
    return this.formatMinutes(Math.abs(this.status.windowSlackMinutes));
  }

  getWindowSlackFigureLabel(): string {
    return this.status.windowSlackMinutes >= 0 ? 'Spare' : 'Over';
  }

  formatMinutes(value: number): string {
    const normalized = Math.max(0, Math.round(value));
    const hours = Math.floor(normalized / 60);
    const minutes = normalized % 60;
    if (hours === 0) {
      return `${minutes}m`;
    }
    if (minutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${minutes}m`;
  }

  cycleProgressLabel(section: DayStatusCycleSection): string {
    return `${section.label} · ${section.doneCount} of ${section.totalCount}`;
  }

  cycleMeterPercent(section: DayStatusCycleSection): number {
    if (section.totalCount <= 0) {
      return 0;
    }
    return Math.round((section.doneCount / section.totalCount) * 100);
  }

  celebrationCopy(): string {
    if (this.celebrationSummary && this.celebrationCycle) {
      const label = this.celebrationCycle === 'LIFE' ? this.cycles.life.label : this.cycles.work.label;
      return `${label} cycle ${this.celebrationSummary.number} complete in ${this.durationLabel(this.celebrationSummary.durationDays)}. Starting a new rotation.`;
    }
    if (this.celebrationCycle === 'LIFE') {
      return `${this.cycles.life.label} cycle complete. Starting a new rotation.`;
    }
    return `${this.cycles.work.label} cycle complete. Starting a new rotation.`;
  }

  lastCompletedCyclesCopy(): string | null {
    const parts: string[] = [];
    if (this.lastCompletedWorkCycle) {
      parts.push(`Last ${this.cycles.work.label} cycle took ${this.durationLabel(this.lastCompletedWorkCycle.durationDays)}`);
    }
    if (this.lastCompletedLifeCycle) {
      const lifeCopy = `${this.cycles.life.label} cycle took ${this.durationLabel(this.lastCompletedLifeCycle.durationDays)}`;
      parts.push(parts.length > 0 ? `last ${lifeCopy}` : `Last ${lifeCopy}`);
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  }

  daysSinceAriaLabel(group: DayStatusCycleGroup): string {
    if (group.daysSince == null) {
      return 'last done never';
    }
    if (group.daysSince === 0) {
      return 'last done today';
    }
    if (group.daysSince === 1) {
      return 'last done 1 day ago';
    }
    return `last done ${group.daysSince} days ago`;
  }

  dropListId(cycle: DayStatusCycleKind): string {
    return `day-status-cycle-${cycle.toLowerCase()}`;
  }

  selectGroup(ordinal: ScheduleType): void {
    this.scheduleTypeSelect.emit(ordinal);
  }

  onGroupKeydown(event: KeyboardEvent, cycle: DayStatusCycleKind, group: DayStatusCycleGroup): void {
    if (!event.altKey || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) {
      return;
    }
    event.preventDefault();
    const section = cycle === 'LIFE' ? this.cycles.life : this.cycles.work;
    const from = section.groups
      .filter((row) => !row.doneThisCycle)
      .findIndex((row) => row.ordinal === group.ordinal);
    if (from < 0) {
      return;
    }
    if (this.moveUndoneGroup(cycle, from, event.key === 'ArrowUp' ? from - 1 : from + 1)) {
      this.pendingFocusOrdinal = group.ordinal;
    }
  }

  onCycleGroupDrop(cycle: DayStatusCycleKind, event: CdkDragDrop<DayStatusCycleGroup[]>): void {
    this.moveUndoneGroup(cycle, event.previousIndex, event.currentIndex);
  }

  moveUndoneGroup(cycle: DayStatusCycleKind, previousIndex: number, currentIndex: number): boolean {
    if (this.reorderDisabled) {
      return false;
    }
    const section = cycle === 'LIFE' ? this.cycles.life : this.cycles.work;
    const undoneOrdinals = section.groups
      .filter((group) => !group.doneThisCycle)
      .map((group) => group.ordinal);
    if (previousIndex < 0 || previousIndex >= undoneOrdinals.length) {
      return false;
    }
    const nextIndex = Math.min(Math.max(currentIndex, 0), Math.max(0, undoneOrdinals.length - 1));
    if (previousIndex === nextIndex) {
      return false;
    }
    const nextUndoneOrdinals = [...undoneOrdinals];
    moveItemInArray(nextUndoneOrdinals, previousIndex, nextIndex);
    const enabledOrdinals = this.status.groups.map((group) => group.ordinal);
    this.reorderGrouping.emit(
      rebuildOrderedIdsForSubset(enabledOrdinals, undoneOrdinals, nextUndoneOrdinals)
    );
    return true;
  }

  private loadExpandedPreference(): boolean {
    try {
      return localStorage.getItem(DAY_STATUS_EXPANDED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private saveExpandedPreference(expanded: boolean): void {
    try {
      localStorage.setItem(DAY_STATUS_EXPANDED_STORAGE_KEY, expanded ? 'true' : 'false');
    } catch {
      // Ignore localStorage access failures.
    }
  }

  private durationLabel(durationDays: number): string {
    const normalized = Math.max(0, Math.floor(durationDays));
    return normalized === 1 ? '1 day' : `${normalized} days`;
  }
}
